import React from 'react';
import type { CaptureProps, ImportAdapter, ImportedItem } from './types';
import { parseBrowserCsv } from './browserCsv';
import { csvHeaderSet } from './detect';
import { readCsvFileTexts } from './captureFiles';

/**
 * Parse a Google Password Manager CSV export.
 *
 * Default Chrome export columns (in order, but we look up by name so reorder
 * is tolerated): `name,url,username,password,note`. Chrome's "name" is the
 * site display name; "url" is a single canonical URL; "note" is free-form.
 *
 * Google doesn't export TOTP, folder, or favorite. Missing columns are
 * silently ignored. The column-mapping work lives in `parseBrowserCsv`, which
 * Firefox's adapter shares — see `browserCsv.ts` for why one parser covers
 * both browsers.
 */
export function parseGoogle(csv: string): ImportedItem[] {
  return parseBrowserCsv(csv, 'google');
}

// ─── CaptureComponent ─────────────────────────────────────────────────────────

const GOOGLE_EXPORT_STEPS = [
  'Open Chrome and visit chrome://password-manager/settings.',
  'Under "Download or import passwords", click Download passwords.',
  'Re-authenticate with your device password when Chrome asks.',
  'Chrome saves a file called "Chrome Passwords.csv" to your downloads.',
];

function GoogleCapture({ onCapture, onError }: CaptureProps) {
  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const texts = await readCsvFileTexts(e.target.files, onError, 'Google Password Manager CSV export');
    if (texts) onCapture(texts);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface border border-divider rounded-xl p-4">
        <h3 className="text-sm font-semibold text-ink mb-2">How to export from Google Password Manager</h3>
        <ol className="text-sm text-ink-secondary list-decimal list-inside space-y-1">
          {GOOGLE_EXPORT_STEPS.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      </div>

      <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed border-divider rounded-xl">
        <svg className="w-10 h-10 text-ink-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <label className="cursor-pointer px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary text-sm font-medium rounded-lg transition-colors">
          Choose CSV file(s)
          <input type="file" accept=".csv" multiple className="hidden" onChange={handleFiles} />
        </label>
      </div>
    </div>
  );
}

/** Chrome / Google Password Manager exports exactly `name,url,username,password,note`.
 *  Require the full set — especially `note` + `name` — so a generic
 *  `url,username,password` sheet falls through to the spreadsheet mapper instead
 *  of being mis-claimed, and so Firefox (checked first) isn't shadowed. */
function detectGoogle(rawInput: string): boolean {
  const h = csvHeaderSet(rawInput);
  return h.has('name') && h.has('url') && h.has('username') && h.has('password') && h.has('note');
}

export const GoogleAdapter: ImportAdapter = {
  id: 'google',
  name: 'Google Password Manager',
  description: 'Import from Google Password Manager (the autofill service built into Chrome).',
  CaptureComponent: GoogleCapture,
  parse: parseGoogle,
  detect: detectGoogle,
};
